import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Button } from "../ui/button";
import { ArrowUpRight, XCircle, Undo2 } from "lucide-react";
import { toast } from "sonner";
import LeadTasksPanel from "./LeadTasksPanel";
import LeadOverviewTab from "./LeadOverviewTab";
import LeadActivityTab from "./LeadActivityTab";
import LeadAssessmentTab from "./LeadAssessmentTab";
import LeadDocumentsTab from "./LeadDocumentsTab";
import ConvertToClientDialog from "./ConvertToClientDialog";
import MarkLostDialog from "./MarkLostDialog";
import { clientStageBadgeClass, CLIENT_TERMINAL_STAGES, LEAD_TERMINAL_STAGES } from "./constants";

export default function LeadDetail({ lead, me, ref_, onClose, onChanged, onConvert }) {
  const [detail, setDetail] = useState(lead);
  const [activity, setActivity] = useState([]);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [lostOpen, setLostOpen] = useState(false);
  const canEdit = me.role === "admin" || detail.owner_id === me.id;
  const isClient = detail.kind === "client";
  const isTerminal = isClient
    ? CLIENT_TERMINAL_STAGES.has(detail.stage)
    : LEAD_TERMINAL_STAGES.has(detail.stage);

  const loadActivity = () => api.get(`/crm/leads/${lead.id}/activity`).then((r) => setActivity(r.data));

  useEffect(() => {
    loadActivity();
  }, [lead.id]);

  async function saveDetail() {
    setSaving(true);
    try {
      const { data } = await api.put(`/crm/leads/${lead.id}`, detail);
      setDetail(data);
      toast.success(isClient ? "Client updated" : "Lead updated");
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function addNote() {
    if (!note.trim()) return;
    try {
      await api.post(`/crm/leads/${lead.id}/notes`, { text: note });
      setNote("");
      loadActivity();
    } catch {
      toast.error("Cannot add note");
    }
  }

  async function del() {
    if (!window.confirm(`Delete ${detail.org_name}?`)) return;
    try {
      await api.delete(`/crm/leads/${lead.id}`);
      toast.success((isClient ? "Client" : "Lead") + " deleted");
      onClose();
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  }

  async function revert() {
    if (!window.confirm(`Move ${detail.org_name} back to leads pipeline?`)) return;
    try {
      const { data } = await api.post(`/crm/leads/${lead.id}/revert-to-lead`);
      setDetail(data);
      toast.success("Moved back to leads");
      loadActivity();
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Revert failed");
    }
  }

  return (
    <>
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="lead-detail">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap">
              {detail.org_name}
              <span
                className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${
                  isClient ? "bg-[#1A7F6E] text-white" : "bg-slate-200 text-slate-700"
                }`}
                data-testid="detail-kind-badge"
              >
                {isClient ? "Client" : "Lead"}
              </span>
              {isClient && (
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${clientStageBadgeClass(detail.stage)}`}>
                  {detail.stage.replaceAll("_", " ")}
                </span>
              )}
              {isClient && detail.package_type && (
                <span className="text-[10px] font-medium bg-[#E6F4F1] text-[#1A7F6E] px-1.5 py-0.5 rounded" data-testid="detail-package-badge">
                  {detail.package_type === "full_package" ? "Full Package" : "Registration only"}
                </span>
              )}
            </DialogTitle>
            <DialogDescription>{detail.contact_name} · {detail.contact_email}</DialogDescription>
            {isClient && detail.converted_by_name && (
              <p className="text-xs text-slate-500 pt-1" data-testid="converted-by-line">
                Converted by <strong className="text-[#0D1B2A]">{detail.converted_by_name}</strong>
                {detail.converted_at && (
                  <> · {new Date(detail.converted_at).toLocaleDateString()}</>
                )}
              </p>
            )}
          </DialogHeader>

          {/* Action bar: Convert / Mark Lost / Revert */}
          {canEdit && !isTerminal && (
            <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 pb-3">
              {!isClient && (
                <Button
                  size="sm"
                  onClick={() => setConvertOpen(true)}
                  className="bg-[#1A7F6E] hover:bg-[#136053] text-white"
                  data-testid="convert-to-client-button"
                >
                  <ArrowUpRight className="w-4 h-4 mr-1.5" />Convert to client
                </Button>
              )}
              {isClient && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={revert}
                  data-testid="revert-to-lead-button"
                >
                  <Undo2 className="w-4 h-4 mr-1.5" />Move back to lead
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={() => setLostOpen(true)}
                className="text-rose-600 border-rose-200 hover:bg-rose-50"
                data-testid="mark-lost-button"
              >
                <XCircle className="w-4 h-4 mr-1.5" />
                {isClient ? "Mark churned" : "Mark lost"}
              </Button>
            </div>
          )}

          <Tabs defaultValue="overview">
            <TabsList>
              <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
              <TabsTrigger value="assessment" data-testid="tab-assessment">Assessment</TabsTrigger>
              {isClient && (
                <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
              )}
              <TabsTrigger value="tasks" data-testid="tab-tasks">Follow-ups</TabsTrigger>
            </TabsList>
            <TabsContent value="overview">
              <LeadOverviewTab
                detail={detail} setDetail={setDetail} me={me} ref_={ref_}
                canEdit={canEdit} onSave={saveDetail} onDelete={del} saving={saving}
              />
            </TabsContent>
            <TabsContent value="activity">
              <LeadActivityTab activity={activity} note={note} setNote={setNote} onAddNote={addNote} />
            </TabsContent>
            <TabsContent value="assessment">
              <LeadAssessmentTab detail={detail} onConvert={onConvert} />
            </TabsContent>
            {isClient && (
              <TabsContent value="documents">
                <LeadDocumentsTab leadId={lead.id} hasAssessment={!!detail.assessment_id} initialHasLogo={!!detail.logo_gridfs_id} />
              </TabsContent>
            )}
            <TabsContent value="tasks" className="mt-4">
              <LeadTasksPanel leadId={lead.id} />
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {convertOpen && (
        <ConvertToClientDialog
          lead={detail}
          packages={ref_.packages || []}
          onClose={() => setConvertOpen(false)}
          onConverted={(updated) => {
            setDetail(updated);
            setConvertOpen(false);
            loadActivity();
            onChanged();
          }}
        />
      )}
      {lostOpen && (
        <MarkLostDialog
          lead={detail}
          reasons={ref_.lost_reasons || []}
          onClose={() => setLostOpen(false)}
          onMarked={(updated) => {
            setDetail(updated);
            setLostOpen(false);
            loadActivity();
            onChanged();
          }}
        />
      )}
    </>
  );
}
